import React, { Suspense } from "react";
import { Navigate, Outlet, type RouteObject } from "react-router-dom";
import { RequireSession } from "../auth/RequireSession";
import { AppShell } from "../shell/AppShell";
import { StateBlock } from "../components/StateBlock";
import { LoginRoute } from "./LoginRoute";
import { VerificationRoute } from "./VerificationRoute";
import { OverviewRoute } from "./OverviewRoute";
import { RequestsRoute } from "./RequestsRoute";
import { ChatRoute } from "./ChatRoute";
import { DocumentsRoute } from "./DocumentsRoute";
import { AccountsRoute } from "./AccountsRoute";
import { SettingsRoute } from "./SettingsRoute";
import { NotificationsRoute } from "./NotificationsRoute";
import { ReportsRoute } from "./ReportsRoute";
import { StatisticsRoute } from "./StatisticsRoute";
import { SecretsRoute } from "./SecretsRoute";
import { SecretViewRoute } from "./SecretViewRoute";
import { RecoverRoute } from "./RecoverRoute";
import { AuthCallbackRoute } from "./AuthCallbackRoute";
import { SetPasswordRoute } from "./SetPasswordRoute";
import { ConfirmEmailChangeRoute } from "./ConfirmEmailChangeRoute";
import { PrivacyRoute } from "./PrivacyRoute";
import { ClientFactsRoute } from "./clientFacts/ClientFactsRoute";
import { NotFoundRoute, RouteErrorRoute } from "./RouteFallbacks";
import { LegacyPathRoute } from "./LegacyPathRoute";
import { LEGACY_ROUTE_PATHS } from "../lib/englishPaths";
import { RouteChrome } from "./RouteChrome";
// The four admin screens, each one its own chunk. See `adminChunks.ts` for why.
import {
  AdminClientsRoute,
  AdminEvidenceRoute,
  AdminInboxRoute,
  AdminNotificationsRoute,
  AdminOperationsRoute,
  AdminRequestsRoute,
  AdminSettingsRoute,
  AsClientRoute
} from "./adminChunks";
import { PersonalLeftOut } from "./asClient/PersonalLeftOut";

/**
 * Every address the portal answers, in one exported table.
 *
 * It lives beside the routes rather than inside `main.tsx` because `main.tsx` mounts the application
 * the moment it is imported, which makes it unimportable from a test. The router's own behaviour is
 * worth testing: whether a bad address produces a Swedish page, whether a render failure is caught,
 * and whether the four admin views stay out of a client's bundle. A table assembled inside the test
 * would prove the test's table.
 */

const authenticated = (element: React.ReactElement) => (
  <RequireSession>{element}</RequireSession>
);

/**
 * What stands there while an admin chunk is on the wire.
 *
 * The same loading block the rest of the portal uses, so a slow connection produces the state the
 * design already has an answer for rather than an empty page. Inside `authenticated`, so a signed-out
 * visitor is redirected before anything is fetched at all.
 */
const deferred = (element: React.ReactElement) => (
  <Suspense fallback={<StateBlock kind="loading" title="Öppnar vyn" />}>{element}</Suspense>
);

/**
 * The application's routes, all of them under one root.
 *
 * The root is pathless and renders nothing but an `Outlet`. It exists to carry `errorElement`, which
 * React Router applies to the nearest boundary above whatever threw. One at the root catches every
 * route, including the ones written after this comment. Boundaries placed per branch would have to be
 * remembered each time a route is added, and the branch somebody forgot is precisely the one that
 * would throw in front of a client.
 *
 * It renders `RouteChrome` rather than a bare `Outlet` for the same reason it carries the error
 * element. Naming the page in the tab and moving focus into it on a destination change are
 * properties of every route, the pages outside the shell included, and the only place to say that
 * once is above all of them.
 */
export const portalRoutes: RouteObject[] = [
  {
    element: (
      <RouteChrome>
        <Outlet />
      </RouteChrome>
    ),
    errorElement: <RouteErrorRoute />,
    children: [
      { path: "/", element: <Navigate to="/overview" replace /> },
      { path: "/login", element: <LoginRoute /> },
      /**
       * The second step of signing in, on its own path rather than inside the login form.
       *
       * Its own path because two different surfaces send people here: the sign-in form, and any
       * admin route reached by a session that has not answered its challenge yet. A step that lived
       * inside `/login` would have nowhere to send the second of those, since that session is already
       * signed in and `/login` bounces it straight back out.
       *
       * Behind the session guard, because there is nothing to verify without a session.
       */
      { path: "/verify", element: authenticated(<VerificationRoute />) },
      /*
        The link mailed to a new sign-in address, added 2026-09-22 in round C. Behind the session
        guard because only the account's own session may confirm it, which the guard turns into
        "sign in first, then come back here" with the token kept. Outside the shell for the same
        reason as `/verify`: one question and one button. English since build round 34 (2026-10-10),
        like every path; the old `/bekrafta-adress` in mails already sent answers 301 here.
      */
      { path: "/confirm-email", element: authenticated(<ConfirmEmailChangeRoute />) },
      /*
        The four screens a mailed link passes through, all of them outside the shell.
        A person on any of them is either not signed in yet or signed in with a session that exists
        only to choose a password, and putting the navigation rail around that would offer a portal
        they cannot use yet.
      */
      { path: "/invite", element: <SetPasswordRoute kind="invite" /> },
      { path: "/new-password", element: <SetPasswordRoute kind="recovery" /> },
      { path: "/recover", element: <RecoverRoute /> },
      { path: "/auth/callback", element: <AuthCallbackRoute /> },
      /*
        The privacy information, 2026-10-06. Outside the session guard because it is owed to a person
        before they sign in as much as after, and outside the shell because it belongs to no tenant.
        Linked from the sign-in page and from Inställningar. `/integritet` until build round 34
        (2026-10-10), which made every path English; the old address answers 301 here.
      */
      { path: "/privacy", element: <PrivacyRoute /> },
      {
        element: authenticated(<AppShell />),
        children: [
          { path: "/overview", element: <OverviewRoute /> },
          { path: "/chat", element: <ChatRoute /> },
          { path: "/requests", element: <RequestsRoute /> },
          { path: "/requests/:number", element: <RequestsRoute /> },
          /*
            Analys och drift, `/analytics` and `/status`, was removed in build round 4 (2026-09-27):
            its figures and the website's status are on Översikt (design.md section 5, plan.md
            sections 5.1, 5.5 and 5.6). Both addresses land on Översikt, as the sketch's `#/analys`
            did, because outage mails and pushes already sent and a client's bookmark carry them, and
            a sent link that says "Sidan finns inte" is a worse answer than the page that now holds it.
          */
          { path: "/analytics", element: <Navigate to="/overview" replace /> },
          { path: "/status", element: <Navigate to="/overview" replace /> },
          /**
           * The ownership and access map, the last of the destinations that was a placeholder.
           *
           * plan.md section 2 job 4 and section 5.9: the client can see the ownership and access
           * map, because ownership alone does not answer which workspace, login, role or recovery
           * path applies during an incident or an offboarding. It reads
           * `public.v_client_account_map`, which excludes Petrovic's own access rows twice over, in
           * the two `select_member` policies and again in the view's own predicate. The screen names
           * that exclusion in words rather than leaving an empty access list to be read as "nobody
           * can reach this account".
           */
          { path: "/accounts", element: <AccountsRoute /> },
          { path: "/documents", element: <DocumentsRoute /> },
          /*
           * Månadsrapporten, build round B2 (2026-10-09). Not a destination of its own: Översikt's
           * report box and the notice lead here.
           */
          { path: "/reports", element: <ReportsRoute /> },
          { path: "/reports/:month", element: <ReportsRoute /> },
          /*
           * Statistik, build round 37 (2026-10-11): every figure month by month. In the navigation only
           * for a client with a website under management (app/shell/AppShell.tsx).
           */
          { path: "/statistics", element: <StatisticsRoute /> },
          { path: "/settings", element: <SettingsRoute /> },
          /**
           * One section of Inställningar, the second build round of 2026-09-24. The sketch split one
           * long page into two groups, the person's own account and the company's, and each section
           * is an address so a notification or the overview can open exactly one. The section names
           * are Swedish because a client reads them in the address bar. `/settings` is the list.
           */
          { path: "/settings/:section", element: <SettingsRoute /> },
          /**
           * The bell's destination, inside the shell and outside the navigation.
           *
           * `design.md` line 139 fixes seven client destinations and `tests/portal-interface.test.tsx`
           * guards the count, so notifications do not become an eighth. They are not a place a person
           * goes looking; they are where the bell already points, and the bell is its own control in
           * the header. Swedish path for the same reason as `/verify` and `/new-password`: it is
           * a URL a client sees and reads.
           */
          { path: "/notifications", element: <NotificationsRoute /> },
          /**
           * `Skicka en hemlighet`, plan.md section 5.10, inside the shell and outside the
           * navigation for the same reason `/notifications` is.
           *
           * `design.md` section 5 fixes seven client destinations and
           * `tests/portal-interface.test.tsx` guards that count, so this is not an eighth. It is
           * also not a place a person browses to: the chat composer points here at the moment
           * somebody is about to type a password into a message, which is the sentence plan.md
           * section 5.2 asks the composer to carry, and `Inställningar` links here for the person
           * who came looking on purpose.
           *
           * Two paths, because the feature has two sides and only one of them is a screen anybody
           * navigates to. `/secrets/:id` is the address the recipient is handed, and the id is the
           * UUID the Worker minted before the row existed. No read model produces it as a link, so a
           * recipient who never opens the notification still reaches it from the list on
           * `/secrets`.
           */
          { path: "/secrets", element: <SecretsRoute /> },
          { path: "/secrets/:id", element: <SecretViewRoute /> },
          /*
            Webbplatsens fakta, build round 19 (2026-10-08): the client's own form for the facts its
            website shows, decision 0503. Inside the shell, which offers it in the navigation only
            when a website has facts. One route object with an optional segment, so moving from the
            list of several websites to one of them keeps the page mounted, as `/admin/clients` does.
            Swedish path, because a client reads it in the address bar.
          */
          { path: "/site-facts/:serviceId?", element: <ClientFactsRoute /> }
        ]
      },
      /**
       * Petrovic's own surface, inside the session guard and outside the client shell.
       *
       * Outside deliberately. `AppShell` is the client's seven destinations, and wrapping a
       * cross-tenant admin view in a navigation that says Översikt, Chatt and Ärenden would present
       * one client's portal around every client's requests. The route carries its own slim frame
       * instead.
       *
       * Being reachable is not being permitted. The view refuses a session that is not a platform
       * admin, and every gateway behind it refuses the same session again on the server.
       */
      { path: "/admin/requests", element: authenticated(deferred(<AdminRequestsRoute />)) },
      { path: "/admin/requests/:requestId", element: authenticated(deferred(<AdminRequestsRoute />)) },
      /**
       * The operations screen, on the same terms as the request board.
       *
       * Swedish path because it is a place rather than a resource, and the two existing Swedish paths
       * were chosen the same way. Everything behind it refuses a session that is not a platform
       * admin, on the server, and the view refuses one too rather than rendering three empty sections
       * that would each read as an all-clear.
       */
      /*
        One tab per address since the build round of 2026-09-24, so a tab is a link and a reload
        opens the same one. The bare path is the first tab.
      */
      { path: "/admin/operations/:tab?", element: authenticated(deferred(<AdminOperationsRoute />)) },
      /*
        Inkorg, the first of the five destinations since 2026-09-24, and `/admin` itself leads there.
        Inställningar and Notiser are Petrovic's own since the same day: until then an admin reached
        his second factor and his sessions only by typing `/settings` into the client's shell.
      */
      { path: "/admin", element: <Navigate to="/admin/inbox" replace /> },
      { path: "/admin/inbox", element: authenticated(deferred(<AdminInboxRoute />)) },
      { path: "/admin/settings", element: authenticated(deferred(<AdminSettingsRoute />)) },
      { path: "/admin/notifications", element: authenticated(deferred(<AdminNotificationsRoute />)) },
      /**
       * The two Batch F destinations, on the same terms as the two above.
       *
       * Swedish paths for the same reason `/admin/operations` has one: they are places rather than
       * resources. `kunder` is the client register and the unified inbox, which is where an operator
       * starts a day. `underlag` is the workload report, the invoice reminders and the customer
       * insight register, which is what Petrovic keeps for himself and never shows a client.
       *
       * Both refuse a session that is not a platform admin, in the view and again in every gateway
       * behind it, rather than rendering empty sections that would each read as an all-clear.
       */
      /**
       * One route with an optional segment, added 2026-09-22, so the open client is in the address.
       * One route rather than two, because two route objects with the same element are two mounts,
       * and moving from the list to a client would throw away the inbox filter and a receipt the
       * erasure panel just left on screen.
       */
      /*
        The tab is the third segment since 2026-09-24, when a client's page became a sidebar and
        eight tabs. Still one route object, for the reason above.
      */
      {
        path: "/admin/clients/:tenantId?/:tab?",
        element: authenticated(deferred(<AdminClientsRoute />))
      },
      /*
        "Visa som kund", build round 41 (2026-10-11, Portal HQ): one client's portal as the client sees
        it, the client's own screens under the view's frame, read through the admin reads and never a
        session as the client. More specific than the route above, so it wins for `as-client`. The
        screens that are one person's own, Notiser and Hemligheter, are left out with one line.
      */
      {
        path: "/admin/clients/:tenantId/as-client",
        element: authenticated(deferred(<AsClientRoute />)),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <OverviewRoute /> },
          { path: "chat", element: <ChatRoute /> },
          { path: "requests", element: <RequestsRoute /> },
          { path: "requests/:number", element: <RequestsRoute /> },
          { path: "accounts", element: <AccountsRoute /> },
          { path: "documents", element: <DocumentsRoute /> },
          { path: "reports", element: <ReportsRoute /> },
          { path: "reports/:month", element: <ReportsRoute /> },
          { path: "statistics", element: <StatisticsRoute /> },
          { path: "settings", element: <SettingsRoute /> },
          { path: "settings/:section", element: <SettingsRoute /> },
          { path: "site-facts/:serviceId?", element: <ClientFactsRoute /> },
          { path: "notifications", element: <PersonalLeftOut /> },
          { path: "secrets/*", element: <PersonalLeftOut /> },
          { path: "secrets", element: <PersonalLeftOut /> }
        ]
      },
      { path: "/admin/records/:tab?", element: authenticated(deferred(<AdminEvidenceRoute />)) },
      /**
       * Everything else, and it is the last entry because it matches everything.
       *
       * Outside the shell and outside the session guard on purpose. See `NotFoundRoute` for why: a
       * signed-out visitor with an old bookmark must be told the address is wrong, not asked to sign
       * in first and then returned to the same dead address.
       */
      /*
        The old Swedish addresses, build round 34 (2026-10-10). The Worker answers them with a 301 first;
        these catch the navigation the service worker serves from its cache. Outside the session guard,
        so the redirect comes before any sign-in and the guard then runs on the English address.
      */
      ...LEGACY_ROUTE_PATHS.map((path) => ({ path, element: <LegacyPathRoute /> })),
      { path: "*", element: <NotFoundRoute /> }
    ]
  }
];
